import React, { createContext, useContext, useEffect, useState } from "react";
import { track } from "../lib/analytics";

const CartContext = createContext(null);
const STORAGE_KEY = "shams_cart_v2"; // v2 to invalidate old non-variant entries

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (err) {
      console.warn("Failed to load cart from localStorage:", err);
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items, hydrated]);

  /**
   * addItem(product, quantity, variant?)
   * variant: {id, label, price, sale_price, stock} or null
   */
  const addItem = (product, quantity = 1, variant = null) => {
    track("add_to_cart", { slug: product.slug, name: product.name });
    setItems((prev) => {
      // Variants distinguish line items
      const variantId = variant?.id || null;
      const existing = prev.find(
        (p) => p.product_id === product.id && (p.variant_id || null) === variantId
      );
      if (existing) {
        return prev.map((p) =>
          p.product_id === product.id && (p.variant_id || null) === variantId
            ? { ...p, quantity: p.quantity + quantity }
            : p
        );
      }
      const price = variant
        ? (variant.sale_price || variant.price)
        : (product.sale_price || product.price);
      return [
        ...prev,
        {
          product_id: product.id,
          slug: product.slug,
          name: product.name,
          price,
          quantity,
          image: product.images?.[0] || null,
          variant_id: variantId,
          variant_label: variant?.label || null,
        },
      ];
    });
  };

  const removeItem = (productId, variantId = null) => {
    setItems((prev) => prev.filter((p) => !(p.product_id === productId && (p.variant_id || null) === variantId)));
  };

  const updateQuantity = (productId, qty, variantId = null) => {
    setItems((prev) =>
      prev
        .map((p) =>
          p.product_id === productId && (p.variant_id || null) === variantId
            ? { ...p, quantity: Math.max(1, qty) }
            : p
        )
        .filter((p) => p.quantity > 0)
    );
  };

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQuantity, clear, subtotal, count }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
